/**
 * 语音输入适配器。
 *
 * 引擎选型说明（这是项目里最容易踩坑的地方，写清楚免得以后忘）：
 *   · 用**浏览器内置识别**（iOS Safari 走苹果自己的服务，国内可用，免费零部署）
 *   · 它是"点击才开麦"的：必须由用户手势触发，不能在 effect 里自动 start
 *   · iOS 上 continuous 模式不可靠，所以按"短句"设计：说一句，出结果，再看下一句
 *   · 微信内置浏览器不支持，所以入口只做在 Safari/PWA 里
 *   · 必须 HTTPS 安全上下文，localhost 例外
 *
 * 识别失败（没权限、环境嘈杂、说了长句被截断）时一律降级：
 * 保留已识别的文字，转成手工草稿让用户改。绝不让用户重说一遍。
 *
 * 这个文件刻意不引入 React —— 适配器只管"说 → 文本"，UI 状态由页面持有。
 */

import { ApiError, parseText } from '../api/client';
import { toManualFallbackDraft, type IntakeAdapter } from './types';

export const voiceAdapter: IntakeAdapter = {
  source: 'voice',
  label: '说一句',
  hint: '「鸡蛋还有六个，西红柿两个」',
  requires: 'none',
  iconPath: 'M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3ZM5 11a7 7 0 0 0 14 0M12 18v3',
};

/* ---- 浏览器语音识别的最小类型声明（lib.dom 里没有，自己补） ---- */

interface SpeechRecognitionAlternativeLike {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}

interface SpeechRecognitionErrorEventLike {
  error: string;
  message?: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** 浏览器是否支持。注意：支持 != 当前环境可用（还需 HTTPS 与权限） */
export function isVoiceSupported(): boolean {
  return getCtor() !== null;
}

/** 是否处于安全上下文（HTTPS 或 localhost）。非安全上下文下 iOS 会直接拒绝开麦。 */
export function isSecureContextOk(): boolean {
  if (typeof window === 'undefined') return false;
  return window.isSecureContext || window.location.hostname === 'localhost';
}

const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': '麦克风权限被拒绝了。到 设置 → Safari → 麦克风 里打开，或者直接手动输入。',
  'service-not-allowed': '系统拒绝了语音服务。iOS 上需要走 Safari（微信里不行）。',
  'audio-capture': '没找到麦克风，检查一下设备。',
  'no-speech': '没听到声音，再说一次试试。',
  network: '语音服务连不上（安卓 Chrome 在国内常见）。还是手动输入吧。',
  aborted: '已取消。',
};

export interface VoiceSessionHandlers {
  /** 实时中间结果，用于让用户看到"听到了什么" */
  onInterim: (text: string) => void;
  /** 最终识别文本 */
  onFinal: (text: string) => void;
  /** 识别层面的失败（还没到解析那一步） */
  onError: (message: string) => void;
}

export interface VoiceSession {
  stop: () => void;
  abort: () => void;
}

/**
 * 开一次识别。返回 null 表示浏览器不支持。
 *
 * 注意：必须在用户点击事件里调用（iOS 的硬要求）。
 */
export function startVoiceSession(handlers: VoiceSessionHandlers): VoiceSession | null {
  const Ctor = getCtor();
  if (!Ctor) {
    handlers.onError('这个浏览器不支持语音识别。用 Safari 打开，或者手动输入。');
    return null;
  }

  const recognition = new Ctor();
  recognition.lang = 'zh-CN';
  // 短句模式：iOS Safari 上 continuous 不可靠，宁可让用户点第二次
  recognition.continuous = false;
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;

  let finalText = '';
  let settled = false;

  recognition.onresult = (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i];
      if (!result) continue;
      const alternative = result[0];
      if (!alternative) continue;
      if (result.isFinal) finalText += alternative.transcript;
      else interim += alternative.transcript;
    }
    if (interim) handlers.onInterim(interim);
    if (finalText) handlers.onInterim(finalText);
  };

  recognition.onerror = (event) => {
    if (settled) return;
    settled = true;
    // 已经识别出一部分就先用着，别浪费用户说过的内容
    if (finalText.trim()) {
      handlers.onFinal(finalText.trim());
      return;
    }
    handlers.onError(ERROR_MESSAGES[event.error] ?? `识别出错（${event.error}）。手动输入吧。`);
  };

  recognition.onend = () => {
    if (settled) return;
    settled = true;
    if (finalText.trim()) handlers.onFinal(finalText.trim());
    else handlers.onError(ERROR_MESSAGES['no-speech'] ?? '没听到内容。');
  };

  try {
    recognition.start();
  } catch (error) {
    handlers.onError(
      `启动麦克风失败：${error instanceof Error ? error.message : '未知原因'}`,
    );
    return null;
  }

  return {
    stop: () => recognition.stop(),
    abort: () => recognition.abort(),
  };
}

/* ------------------------------------------------------------------ */
/* 识别文本 → IntakeDraft                                              */
/* ------------------------------------------------------------------ */

/**
 * 把识别出来的文字交给服务端解析。
 * 服务端未配模型时会自己降级到本地规则解析（见 modules/parse），
 * 这里只需处理"网络层完全挂了"这一种情况。
 */
export async function draftFromSpeechText(text: string) {
  try {
    return await parseText(text, 'voice');
  } catch (error) {
    const reason =
      error instanceof ApiError ? `${error.message} ` : '解析服务不可用。';
    return toManualFallbackDraft('voice', text, reason);
  }
}
